import React, { useEffect, useRef, useState } from "react";
import SectionContainer from "../../../Homepage/SectionContainer";
import BreadCrumb from "../../../component/BreadCrumb/BreadCrumb";
import { Col, Container, Row } from "reactstrap";
import Fullform from "../../../component/FullForm/Fullform";
import ContactService from "../../../services/ContactService";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import AdvancedGoogleMap from "../../../components/Map/AdvancedGoogleMap";

const Contact = () => {
  const mapHeight = useRef(null);
  const [height, setHeight] = useState(0);
  const location = useLocation();
  const selectedOffice = location.state?.selectedOffice;
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setIsMapLoaded(true);
      } else {
        // Retry after a short delay
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  // Helper function to convert Google Maps URL to embed format
  const convertToEmbedUrl = (url) => {
    if (!url) return null;

    if (url.includes('/embed')) {
      return url;
    }

    if (url.includes('google.com/maps')) {
      try {
        const placeMatch = url.match(/\/maps\/place\/([^/@]+)/);
        const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
        const preciseCoordMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);

        let lat, lng;

        if (preciseCoordMatch) {
          lat = preciseCoordMatch[1];
          lng = preciseCoordMatch[2];
        } else if (coordMatch) {
          lat = coordMatch[1];
          lng = coordMatch[2];
        }

        if (lat && lng) {
          if (placeMatch) {
            const placeName = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
            return `https://maps.google.com/maps?q=${encodeURIComponent(placeName)}&t=&z=17&ie=UTF8&iwloc=&output=embed`;
          }

          return `https://maps.google.com/maps?q=${lat},${lng}&t=&z=17&ie=UTF8&iwloc=&output=embed`;
        }
      } catch (e) {
        console.error('Error parsing Google Maps URL:', e);
      }
    }

    return url;
  };

  const { data: mapData } = useQuery({
    queryKey: ["mapDataById", selectedOffice?.id],
    queryFn: async () => {
      return await ContactService.getById(selectedOffice.id);
    },
    enabled: !!selectedOffice?.id,
  });

  const fetchData = async () => {
    const response = await ContactService.getList();
    return response;
  };

  const {
    data: countryData,
  } = useQuery({
    queryKey: ["mapData"],
    queryFn: fetchData,
  });

  const filteredCountries = countryData?.items?.reduce((acc, item) => {
    // Convert country name to uppercase for comparison
    const countryKey = item.country.toUpperCase();

    const displayCountryName = item.country.charAt(0).toUpperCase() + item.country.slice(1).toLowerCase();
    
    if (!acc[countryKey]) {
      acc[countryKey] = {
        country: displayCountryName, // Keep original country name for display
        cities: [],
      };
    }

    const cityExists = acc[countryKey].cities.some(
      (c) => c.name === item.city
    );

    if (!cityExists) {
      acc[countryKey].cities.push({
        id: item.id,
        name: item.city,
        googleMapUrl: item.googleMapUrl,
        address: item.address,
        email: item.email,
        phone: item.phone,
        country: item.country,
        openingHours: item.openingHours,
      });
    }

    return acc;
  }, {});

  const result = filteredCountries ? Object.values(filteredCountries) : [];

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Set initial selection based on location state or default to first city
  useEffect(() => {
    // Only run this once when data is available and not yet initialized
    if (isInitialized || !result || result.length === 0) return;

    if (selectedOffice) {
      // If coming from Map component with selected office
      setSelectedLocation(selectedOffice.label);
      setIsInitialized(true);
    } else if (mapData) {
      // If API returned data
      setSelectedLocation(mapData.city);
      setIsInitialized(true);
    } else {
      // Fallback to first city if no selection is provided
      const firstCity = result[0].cities[0];
      setSelectedLocation(firstCity.name);
      setIsInitialized(true);
    }
  }, [selectedOffice, mapData, result, isInitialized]);

  useEffect(() => {
    if (mapHeight.current) {
      setHeight(mapHeight.current.offsetHeight);
    }
  }, [result]);

  const offices = countryData?.items?.map(item => ({
    id: item.id,
    name: item.city,              // Used for marker title & selection
    city: item.city,
    country: item.country,
    googleMapUrl: item.googleMapUrl,
    address: item.address,        // if exists
    phone: item.phone,            // if exists
    email: item.email,            // if exists
  })) || [];


  const ArrowIcon = ({ color }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 18.31 15.01"
      width="18"
    >
      <path
        d="M.25,8.73h13.68l-3.95,3.95c-.48.48-.48,1.25,0,1.73.48.48,1.25.48,1.73,0l6-6c.25-.25.36-.57.35-.89,0-.32-.11-.65-.35-.89L11.7.61c-.48-.48-1.25-.48-1.73,0-.48.48-.48,1.25,0,1.73l3.95,3.95H.25"
        style={{
          fill: color,
          stroke: color,
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeWidth: "0.5px",
        }}
      />
    </svg>
  );

  return (
    <>
      <SectionContainer>
        <Container fluid className="p-0 m-0">
          <Row>
            <Col>
              <BreadCrumb
                items={[
                  { title: "HOME", link: "/" },
                  { title: "CONTACT", },
           
                ]}
                instituition={false}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <h1 className="font-level-4 p-0 mt-2">OUR OFFICES</h1>
            </Col>
          </Row>

          <Row
            className="d-flex justify-content-between align-items-start "
            style={{ marginTop: "4.5rem", marginBottom: "12.5rem" }}
          >
            <Col lg={3} sm={12} ref={mapHeight}>
              {result?.map((countryObj) => (
                <div key={countryObj.country}>
                  <h3
                    className="font-level-10 mb-3"
                    style={{ color: "#687c86" }}
                  >
                    {countryObj.country}
                  </h3>
                  <div className="mb-4">
                    {countryObj.cities?.map((cityObj, index) => (
                      <div
                        key={index}
                        className="d-flex justify-items-center align-items-center gap-2 "
                        onClick={() => {
                          setSelectedLocation(cityObj.name);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="mb-3">
                          <ArrowIcon
                            color={
                              cityObj.name === selectedLocation
                                ? "#7D178F"
                                : "#687c86"
                            }
                          />
                        </div>
                        <p
                          className={
                            cityObj.name === selectedLocation
                              ? "font-level-10 p-0 mb-3"
                              : "font-level-2 p-0 mb-3"
                          }
                          style={{
                            cursor: "pointer",
                            color:
                              cityObj.name === selectedLocation
                                ? "#7D178F"
                                : "#687c86",
                          }}
                        >
                          {cityObj.name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Col>
            <Col
              lg={9}
              sm={12}
              className="d-flex "
              style={{ height: height ? `${height}px` : "auto", minHeight: "500px" }}
            >
              <div style={{ width: "100%", height: "100%" }}>
                {!isMapLoaded ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <p style={{ color: "#687c86" }}>Loading map...</p>
                  </div>
                ) : result && result.length > 0 ? (
                  <AdvancedGoogleMap
                    offices={result.flatMap(country => 
                      country.cities.map(city => ({
                        ...city,
                        country: country.country
                      }))
                    )}
                    selectedLocation={selectedLocation}
                    onMarkerClick={(office) => setSelectedLocation(office.name)}
                  />
                ) : null}
              </div>
            </Col>
          </Row>
        </Container>
      </SectionContainer>
      <SectionContainer mainId="careerpage">
        <Row>
          <Col style={{ marginBottom: "3rem", marginTop: "0rem" }}>
            <h1 className="career-sub-heading lh-1 pb-3">
              Have Question? <br /> We're Here to Help.
            </h1>
            <p className="career-desc-font">
              Fill out the form below and our team will get back to you as soon
              as possible
            </p>
          </Col>
        </Row>

        <Fullform
          closeFormat="gold"
          closingHeading="FOR REACHING OUT! OUR TEAM WILL GET BACK TO YOU SHORTY"
          fullName={true}
          email={true}
          phone={true}
          message={true}
          formOrigin={7}
        />
      </SectionContainer>
    </>
  );
};

export default Contact;